import { m } from '@sohanscript/i18n';
import { formatNumber } from '@sohanscript/lib/utils/format';
import { PageHero } from '@sohanscript/ui/page-hero';
import { createFileRoute } from '@tanstack/react-router';
import { Hydrate } from '@tanstack/react-start';
import { idle } from '@tanstack/react-start/hydration';
import { z } from 'zod';
import { Cta } from '#/components/cta.tsx';
import { GoToTop } from '#/components/layout/goto-top.tsx';
import { ServiceLedger } from '@/components/services/service-ledger';
import { seo, siteConfig } from '@/lib/config/site';
import { services, type ServiceCategory } from '@/lib/data/services';
import { localizeServices } from '@/lib/data/services-i18n';

const categorySlugs = [
	'ai',
	'mobile',
	'automation',
	'data',
	'backend',
	'full-stack',
	'extensions',
] as const;

type CategorySlug = (typeof categorySlugs)[number];

const slugToCategory: Record<CategorySlug, ServiceCategory> = {
	ai: 'AI',
	mobile: 'Mobile',
	automation: 'Automation',
	data: 'Data',
	backend: 'Backend',
	'full-stack': 'Full-Stack',
	extensions: 'Extensions',
};

const categoryToSlug: Record<ServiceCategory, CategorySlug> = {
	AI: 'ai',
	Mobile: 'mobile',
	Automation: 'automation',
	Data: 'data',
	Backend: 'backend',
	'Full-Stack': 'full-stack',
	Extensions: 'extensions',
};

export const Route = createFileRoute('/_main/services')({
	component: ServicesPage,
	validateSearch: z.object({
		c: z.enum(categorySlugs).optional().catch(undefined),
	}),
	head: () => {
		const tags = seo({
			title: m.nav_services(),
			description: m.svc_seo_desc(),
			url: `${siteConfig.canonical}/services`,
		});
		return { meta: tags.meta, links: tags.links };
	},
});

const protocolStages = [
	{ id: 'STAGE_01', labelKey: 'discovery' },
	{ id: 'STAGE_02', labelKey: 'pipeline' },
	{ id: 'STAGE_03', labelKey: 'infra' },
	{ id: 'STAGE_04', labelKey: 'delivery' },
] as const;

const stageLabels = {
	discovery: m.svc_stage_discovery,
	pipeline: m.svc_stage_pipeline,
	infra: m.svc_stage_infra,
	delivery: m.svc_stage_delivery,
} satisfies Record<(typeof protocolStages)[number]['labelKey'], () => string>;

function ProtocolStrip() {
	return (
		<section className="mt-16 border-primary/20 border-y py-8">
			<div className="mb-6 flex items-center justify-between font-mono text-2xs text-muted-foreground uppercase tracking-widest">
				<span className="text-primary">~/protocol</span>
				<span>
					{m.svc_protocol_heading({
						count: formatNumber(protocolStages.length),
					})}
				</span>
			</div>
			<ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
				{protocolStages.map((stage, index) => (
					<li key={stage.id} className="flex flex-col gap-2">
						<span className="font-mono text-primary/50 text-xs tabular-nums">
							{stage.id}
						</span>
						<span className="font-medium text-foreground/90 text-sm">
							{stageLabels[stage.labelKey]()}
						</span>
						<span aria-hidden="true" className="font-mono text-foreground/30">
							{'▮'.repeat(index + 1)}
						</span>
					</li>
				))}
			</ol>
		</section>
	);
}

function ServicesPage() {
	const all = localizeServices();
	const { c } = Route.useSearch();
	const navigate = Route.useNavigate();
	return (
		<>
			<section className="container pt-10 pb-20 lg:py-20">
				<PageHero
					path="services"
					status="[STATUS: READY_FOR_DISPATCH]"
					sub={m.svc_intro()}
					meta={m.svc_note({ count: formatNumber(services.length) })}
					metaRight="SYS_LOAD: 38.4% // 0xPROTO"
				>
					{m.svc_header_title()}
				</PageHero>

				<ServiceLedger
					services={all}
					filter={c ? slugToCategory[c] : null}
					onFilterChange={(next) =>
						navigate({
							search: (prev) => ({
								...prev,
								c: next ? categoryToSlug[next] : undefined,
							}),
							resetScroll: false,
						})
					}
				/>

				<ProtocolStrip />
			</section>
			<Hydrate when={idle()}>
				<Cta className="container" />
			</Hydrate>
			<GoToTop />
		</>
	);
}
